import React, { useState } from 'react';
import { ChevronLeft, Plus, CreditCard, CheckCircle, Edit, Trash2, AlertCircle } from 'lucide-react';

export default function BankCardListPage({ bankCards, selectedCardId, onBack, onSelect, onAdd, onEdit, onDelete }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const handleDelete = (cardId) => {
    setShowDeleteConfirm(cardId);
  };

  const confirmDelete = () => {
    if (showDeleteConfirm) {
      onDelete(showDeleteConfirm);
      setShowDeleteConfirm(null);
    }
  };

  const getBankColor = (bankName) => {
    const colors = {
      '工商银行': 'from-red-500 to-red-600',
      '建设银行': 'from-blue-500 to-blue-600',
      '农业银行': 'from-green-500 to-green-600',
      '中国银行': 'from-red-600 to-red-700',
      '招商银行': 'from-red-400 to-red-500',
      '交通银行': 'from-blue-600 to-blue-700',
      '浦发银行': 'from-blue-400 to-blue-500',
      '中信银行': 'from-red-500 to-red-600',
      '光大银行': 'from-orange-500 to-orange-600',
      '民生银行': 'from-blue-500 to-blue-600',
      '平安银行': 'from-orange-400 to-orange-500',
      '华夏银行': 'from-red-500 to-red-600',
    };
    return colors[bankName] || 'from-slate-500 to-slate-600';
  };

  const getBankIcon = (bankName) => {
    return bankName?.charAt(0) || '银';
  };

  return (
    <div className="bg-slate-50 min-h-full pb-24">
      <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 px-4 py-3 border-b border-slate-200 flex items-center shadow-sm">
        <button onClick={onBack} className="p-1 -ml-2">
          <ChevronLeft size={24} className="text-slate-700" />
        </button>
        <span className="font-bold text-slate-800 ml-2 text-lg">选择银行卡</span>
      </div>

      <div className="px-4 py-4 space-y-4">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-md">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard size={16} />
            <span className="text-sm font-medium">收款账户</span>
          </div>
          <div className="font-bold text-xl">选择或添加银行卡</div>
        </div>

        {bankCards && bankCards.length > 0 ? (
          <div className="space-y-3">
            {bankCards.map((card) => (
              <div
                key={card.id}
                onClick={() => onSelect(card.id)}
                className={`relative overflow-hidden rounded-xl shadow-sm cursor-pointer transition-all ${
                  selectedCardId === card.id ? 'ring-2 ring-blue-500' : 'border border-slate-200'
                }`}
              >
                <div className={`bg-gradient-to-r ${getBankColor(card.bankName)} p-4 text-white`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold">
                        {getBankIcon(card.bankName)}
                      </div>
                      <div>
                        <div className="font-bold text-lg">{card.bankName}</div>
                        <div className="text-xs text-white/80">储蓄卡</div>
                      </div>
                    </div>
                    {selectedCardId === card.id && (
                      <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                        <CheckCircle size={14} className="text-green-500" />
                      </div>
                    )}
                  </div>
                  <div className="font-mono text-lg tracking-wider mb-2">
                    **** **** **** {card.cardNumber.slice(-4)}
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <div className="text-white/60 text-xs">开户支行</div>
                      <div className="font-medium text-xs">{card.branch || '-'}</div>
                    </div>
                    <div>
                      <div className="text-white/60 text-xs">有效期</div>
                      <div className="font-medium">{card.expiryDate}</div>
                    </div>
                  </div>
                </div>
                <div className="bg-white px-4 py-2 flex items-center justify-between border-t border-slate-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(card);
                    }}
                    className="flex items-center gap-1 text-sm text-slate-600 hover:text-blue-600 transition-colors"
                  >
                    <Edit size={14} />
                    编辑
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(card.id);
                    }}
                    className="flex items-center gap-1 text-sm text-slate-600 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={14} />
                    删除
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard size={32} className="text-slate-400" />
            </div>
            <div className="text-slate-600 font-medium mb-2">暂无银行卡</div>
            <div className="text-sm text-slate-400">请添加您的收款银行卡</div>
          </div>
        )}

        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertCircle size={16} className="text-amber-500 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-700 leading-relaxed">
              请确保银行卡信息准确无误，收款账户将用于订单结算。如需更换收款账户，可在订单结算前修改。
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-16 left-0 right-0 z-20 flex justify-center pointer-events-none">
        <div className="w-full max-w-md bg-white border-t border-slate-200 p-4 pointer-events-auto">
          <button
            onClick={onAdd}
            className="w-full py-3.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <Plus size={20} />
            添加银行卡
          </button>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl">
            <div className="p-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle size={24} className="text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 text-center mb-2">确认删除</h3>
              <p className="text-sm text-slate-600 text-center mb-6">
                确定要删除这张银行卡吗？删除后无法恢复。
              </p>
              <div className="space-y-3">
                <button
                  onClick={confirmDelete}
                  className="w-full py-3 bg-red-500 text-white rounded-xl font-bold text-base hover:bg-red-600 transition-all active:scale-[0.98]"
                >
                  确认删除
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="w-full py-3 bg-slate-100 text-slate-600 rounded-xl font-bold text-base hover:bg-slate-200 transition-all active:scale-[0.98]"
                >
                  取消
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
